<template>
  <div>
    <div class="primary-background py-3">
      <div class="container d-flex justify-content-end">
        <ol class="breadcrumb text-align-right">
          <li class="breadcrumb-item"><a href="/">Home</a></li>
          <li v-if="!loadingProgram" class="breadcrumb-item">
            <router-link :to="{ name: 'programs' }">
              {{ loadingProgram ? '...' : 'Programs' }}
            </router-link>
          </li>
          <li class="breadcrumb-item active text-truncate" style="max-width: 280px;">
            {{ loadingProgram ? '...' : programDetails.name }}
          </li>
        </ol>
      </div>
    </div>

    <custom-loader v-if="loadingProgram" />
    <div v-else>
      <div v-if="!showLoginPage || isAuthenticated" class="primary-background mb-4">
        <div class="container pb-5">
          <div class="d-flex justify-content-end align-items-center">
            <div v-if="canEditCms">
              <i
                v-b-tooltip.hover
                class="fa fa-toggle-on edit-icon" title="Program Apply Now Button Settings"
                @click="openApplyNowModal"
              />
              <i
                v-b-tooltip.hover
                v-b-modal="'programSettingsModal'" class="fa fa-cog edit-icon" title="Quick Edit"
              />
              <a :href="fetchLegacyUrl(`/programs/${programDetails.id}/edit`)">
                <i v-b-tooltip.hover class="fa fa-edit edit-icon" title="Full Edit" />
              </a>
            </div>
            <div ref="parentQuickLinkContainer" class="ml-2">
              <custom-quick-link-section
                :quick-link-list="quickLinkList"
                :show-admin-tools="true"
                :event-type="'programs'"
                :event-quick-link="programDetails.quick_links"
                :event-datas="programDetails"
              />
            </div>
          </div>
          <b-alert v-if="programDetails.accept_program_member" show variant="info">
            <div class="lyon-display-medium-18">
              <h3>You have been invited to be a member of this program: {{ programDetails.name }}</h3>
              <span v-if="!programDetails.is_past">
                To accept this invitation, please click on the button below and complete the registration process.
              </span>
            </div>
            <div v-if="!programDetails.is_past" class="mt-3">
              <a
                v-if="isAuthenticated && getUserOrcid"
                :href="fetchLegacyUrl(`/programs/${this.$route.params.id}/accept`)"
              >
                <button class="btn btn-primary btn-md">
                  Accept/Register
                </button>
              </a>
              <button v-else class="btn btn-primary btn-md" @click="signInWithOrcid()">
                A linked ORCID identifier is required to register
              </button>
            </div>
          </b-alert>
          <b-alert v-if="programDetails.program_signed_in" show variant="info">
            <span class="lyon-display-medium-18">You are already registered for this program.</span>
          </b-alert>
          <div class="row justify-content-between">
            <div class="col-lg-7 col-xl-4 mb-4">
              <div class="custom-featured-image">
                <img :src="fetchImage(programDetails.image_url, 400, 300)" onerror="this.onerror=null;this.src='/img/SLMath/SLMath_default.png';">
                <div class="text-muted regular-12" v-html="programDetails.image_caption_name" />
              </div>
            </div>

            <div class="col-lg-5 col-xl-7 my-auto">
              <div v-if="programDetails.status" :class="eventStatusType(programDetails.status)">
                <span class="regular-14 typo-color-white text-uppercase">
                  {{ programDetails.status }}
                </span>
              </div>
              <div class="text-muted regular-12 mb-2">
                <span>{{ moment(programDetails.from, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do, YYYY') }}</span>
                <span> - </span>
                <span>{{ moment(programDetails.to, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do, YYYY') }}</span>
              </div>
              <div class="regular-32 mb-2">
                <i
                  v-if="!programDetails.visible_on_public_site"
                  v-b-tooltip.hover.top
                  title="SLMath Only"
                  class="fas fa-lock"
                />
                {{ programDetails.name }}
              </div>
              <div class="medium-14 typo-color-blue mb-2">
                <i class="fas fa-envelope" />
                <a :href="`mailto:${programDetails.contact}`"> {{ programDetails.contact }}</a>
              </div>
              <div v-if="isAuthenticated && applyNowButtonVisible" class="mt-3">
                <a
                  v-if="applyNowButtonVisible.is_visible && applyNowButtonVisible.button_text"
                  :href="applyNowButtonVisible.button_url"
                >
                  <button class="btn btn-primary btn-md">
                    {{ applyNowButtonVisible.button_text }}
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isAuthenticated && showLoginPage" class="primary-background mb-4">
        <div class="container">
          <div class="box blur-content">
            <img src="/img/SLMath/SLMath_default.png">
          </div>
          <div class="overlay">
            <signin :halt-redirect="true" />
          </div>
        </div>
      </div>

      <div class="container">
        <tabs-section
          v-if="!loadingProgram && !loadingApplyButton"
          :datas="programDetails"
          :tab-modal="applyNowButtonModal"
          :loading-program="loadingProgram"
        />
      </div>

      <b-modal
        id="programApplyNowButtonSettingsModal"
        ref="programApplyNowButtonSettingsModal"
        size="lg"
        title="Program Apply Now Button Settings"
        centered
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
      >
        <detail-apply-button-form
          :type="'programs'"
          :ref-id="'programApplyNowButtonSettingsModal'"
          :modal-data="applyNowButtonModal"
        />
      </b-modal>

      <b-modal
        id="programSettingsModal"
        ref="programSettingsModal"
        size="xl"
        title="Program Quick Edit"
        centered
        hide-footer
        no-enforce-focus
        no-close-on-backdrop
        no-close-on-esc
      >
        <program-edit-form
          :modal-data="programDataForm"
          :tag-listing="tagListing"
          @updateProgramData="updateProgramData"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import TabsSection from "./ProgramDetailTabsSection.vue"
import mixin from "@/utils/mixin.js"
import CustomLoader from "@/components/CommonPages/CustomLoader.vue"
import { mapGetters } from "vuex"
import Signin from "@/pages/Signin.vue"
import DetailApplyButtonForm from "@/components/CommonPages/DetailApplyButtonForm/DetailApplyButtonForm.vue"
import ProgramEditForm from "./ProgramEditForm.vue"
import CustomQuickLinkSection from "@/components/QuickLink/CustomQuickLinkSection.vue"

export default {
	name: "CurrentFeaturedProgramDetailsSection",
	components: { TabsSection, CustomLoader, Signin, DetailApplyButtonForm, ProgramEditForm, CustomQuickLinkSection },
	mixins: [mixin],
	metaInfo: {
		title: "Programs Detail",
	},
	props: ["programType"],
	data() {
		return {
			programDetails: null,
			loadingProgram: true,
			showLoginPage: false,
			applyNowButtonModal: null,
			applyNowButtonVisible: null,
			tagListing: [],
			programDataForm: null,
			quickLinkList: [],
			loadingApplyButton: true,
		}
	},
	computed: {
		...mapGetters(["canEditCms", "isAuthenticated", "getUserOrcid"]),
	},
	watch: {
		isAuthenticated() {
			this.loadProgramDetails()
		},
	},
	mounted() {
		this.$root.$on("programApplyNowButtonSettingsModal", () => {
			this.$refs.programApplyNowButtonSettingsModal.hide()
			this.applyNowRights()
		})
	},
	async created() {
		await this.loadProgramDetails()
		await this.applyNowRights()
		await this.loadTagListingData()
	},
	updated() {
		MathJax.typeset()
	},
	methods: {
		updateProgramData(isHide) {
			if (isHide) {
				this.$refs.programSettingsModal.hide()
			} else {
				this.$refs.programSettingsModal.hide()
				this.loadProgramDetails()
			}
		},
		loadProgramDetails() {
			this.loadingProgram = true
			this.$http
				.get(`${process.env.VUE_APP_API_URL}/programs/${this.$route.params.id}.json`, { validateStatus: () => true })
				.then((res) => {
					if (res.status === 404) {
						this.$router.replace({ name: "home" })
					} else if (!this.checkValidateStatus(res.status)) {
						if (!this.checkExternalEventRedirect(res.data.external_url, res.data.redirect_to_external_url)) {
							this.programDetails = res.data
							this.programDataForm = { ...res.data }
							this.loadDescScript(res.data.description)
							if (res.data.quick_link_hdr_id !== null) this.quickLinkList = res.data.menu
							this.loadingProgram = false
						}
					}
				})
		},
		loadTagListingData() {
			this.$http.get(`${process.env.VUE_APP_API_URL}/workshops/tag_listing`).then((res) => {
				if (res !== null) this.tagListing = res.data
			})
		},
		async openApplyNowModal() {
			await this.applyNowRights()
			this.$refs.programApplyNowButtonSettingsModal.show()
		},
		async applyNowRights() {
			this.loadingApplyButton = true
			await this.$http
				.get(`${process.env.VUE_APP_API_URL}/program_admin_toggle_rights/show_toggle`, {
					params: {
						page_name: "programs",
					},
				})
				.then((res) => {
					this.applyNowButtonVisible = { ...res.data }
					this.applyNowButtonModal = res.data
					this.loadingApplyButton = false
				})
		},
	},
}
</script>

<style scoped lang="scss">
.blur-content{
  -webkit-filter: blur(4px);
  -moz-filter: blur(4px);
  -o-filter: blur(4px);
  -ms-filter: blur(4px);
  filter: blur(4px);
}
.box {
  width: 100%;
  height: 100%;
  position: absolute;
  opacity: 0.7;
  user-select: none;
  pointer-events: none;
  @media (max-width: 64rem) {
    display: none;
  }
}
</style>
